import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store/store';
import { BillingView, EBillingSubTab, IBillingState } from 'types/Billing';
import {
	changeBillingStatusThunk,
	downloadMembersAggregationBillingThunk,
	getAdminBillingStatusesThunk,
	getBillingTypologiesThunk,
	getClaimsBillingStatusesThunk,
	getClaimsBillingThunk,
	getCSVBillingExportThunk,
	getMembersAggregationBillingThunk,
	getMembersBillingThunk,
	getPlanCodesThunk,
	rebillClaimThunk,
} from './billingThunk';

const initialState: IBillingState = {
	pages: { claims: 1, aggregation: 1, members: 1 },
	masterSort: null,
	totalPages: 0,
	tabSelected: 'claims',
	subTabSelected: EBillingSubTab.CLAIMS,
	claimsBilling: [],
	memberAggregationBilling: [],
	membersBilling: [],
	claimsBillingStatuses: [],
	adminBillingStatuses: [],
	groups: [],
	billingTypologies: [],
	membersBillingMonths: [],
	planCodes: [],
	planCodesDomain: [],
};

export const getClaimsBilling = createAsyncThunk(
	'billing/getClaimsBilling',
	getClaimsBillingThunk,
);

export const getClaimsBillingStatuses = createAsyncThunk(
	'billing/getClaimsBillingStatuses',
	getClaimsBillingStatusesThunk,
);

export const getAdminBillingStatuses = createAsyncThunk(
	'billing/getAdminBillingStatuses',
	getAdminBillingStatusesThunk,
);

export const getBillingTypologies = createAsyncThunk(
	'billing/getBillingTypologies',
	getBillingTypologiesThunk,
);

export const changeBillingStatus = createAsyncThunk(
	'billing/changeBillingStatus',
	changeBillingStatusThunk,
);

export const getCSVBillingExport = createAsyncThunk(
	'billing/getCSVExport',
	getCSVBillingExportThunk,
);

export const rebillClaim = createAsyncThunk(
	'billing/rebillClaim',
	rebillClaimThunk,
);

export const getMembersBilling = createAsyncThunk(
	'billing/getMembersBilling',
	getMembersBillingThunk,
);

export const getMembersAggregationBilling = createAsyncThunk(
	'billing/getMembersAggregationBilling',
	getMembersAggregationBillingThunk,
);

export const downloadMembersAggregationBilling = createAsyncThunk(
	'billing/downloadMembersAggregationBilling',
	downloadMembersAggregationBillingThunk,
);

export const getPlanCodes = createAsyncThunk(
	'billing/getPlanCodes',
	getPlanCodesThunk,
);

const billingSlice = createSlice({
	name: 'billing',
	initialState,
	reducers: {
		changePage: (
			state,
			{ payload }: PayloadAction<{ view: BillingView; page: number }>,
		) => {
			state.pages[payload.view] = payload.page;
		},
		// tab/search change: every view starts from page 1, saved sort is dropped
		resetBillingPages: state => {
			state.pages = initialState.pages;
			state.masterSort = null;
		},
		// sort/search in the current view: only that view goes back to page 1
		resetBillingPage: (state, { payload }: PayloadAction<BillingView>) => {
			state.pages[payload] = 1;
		},
		// opening the detail: it starts from page 1, the master keeps its page
		openMembersDetail: (
			state,
			{ payload }: PayloadAction<IBillingState['masterSort']>,
		) => {
			state.masterSort = payload;
			state.pages.members = 1;
		},
		setTabSelected: (state, action) => {
			state.tabSelected = action.payload;
		},
		setSubTabSelected: (state, action) => {
			state.subTabSelected = action.payload;
		},
		resetClaimsBilling: state => {
			state.claimsBilling = [];
			state.membersBilling = [];
			state.memberAggregationBilling = [];
		},
		resetBillingState: state => ({
			...initialState,
			claimsBillingStatuses: state.claimsBillingStatuses,
			adminBillingStatuses: state.adminBillingStatuses,
			billingTypologies: state.billingTypologies,
			planCodesDomain: state.planCodesDomain,
		}),
	},
	extraReducers: builder => {
		builder
			.addCase(getClaimsBilling.fulfilled, (state, { payload }) => {
				state.claimsBilling = payload.content;
				state.totalPages = payload.totalPages;
			})
			.addCase(getMembersAggregationBilling.fulfilled, (state, { payload }) => {
				state.memberAggregationBilling = payload.content;
				state.totalPages = payload.totalPages;
			})
			.addCase(getClaimsBillingStatuses.fulfilled, (state, { payload }) => {
				state.claimsBillingStatuses = payload;
			})
			.addCase(getAdminBillingStatuses.fulfilled, (state, { payload }) => {
				state.adminBillingStatuses = payload;
			})
			.addCase(getBillingTypologies.fulfilled, (state, { payload }) => {
				state.billingTypologies = payload;
			})
			.addCase(getMembersBilling.fulfilled, (state, { payload }) => {
				state.membersBilling = payload.content;
				state.totalPages = payload.totalPages;
			})
			.addCase(getPlanCodes.fulfilled, (state, { payload }) => {
				state.planCodes = payload;
				state.planCodesDomain = payload;
			});
	},
});

export const {
	changePage,
	resetBillingPage,
	resetBillingPages,
	openMembersDetail,
	setTabSelected,
	setSubTabSelected,
	resetClaimsBilling,
	resetBillingState,
} = billingSlice.actions;

export const selectIsMembers = (state: RootState) =>
	state.billing.subTabSelected === EBillingSubTab.MEMBERS;

export default billingSlice.reducer;
